/**
 * Navigation contributed by the AUTH layer.
 *
 * ── Why a registry rather than one list ───────────────────────────────────
 * Every layer that adds signed-in pages also needs to add sidebar entries for
 * them. The obvious approach — one NAV array in the shell — means each new
 * layer must edit the shell, and a layer cannot edit a file it does not own
 * without shipping a whole copy of it. That is how this codebase briefly ended
 * up with two 340-line copies of the same shell kept in sync by hand.
 *
 * Instead each layer drops a `nav.<layer>.ts` here exporting its own items.
 * AppShell globs them at BUILD time and merges. Adding a layer is adding one
 * small file; the shell is never touched and cannot drift.
 */
import { LayoutDashboard } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Exact-match the route. Needed for index routes like /dashboard. */
  end?: boolean;
  /** Hide from anyone without this role. Presentation only — see AppShell. */
  role?: string;
  /**
   * Bloomneo staff only — hidden from a customer's own owner.
   *
   * `role` cannot express this. It is matched against user.role, and both
   * admin.system (staff) and admin.tenant (a customer's owner) are "admin";
   * only the LEVEL separates them. So an item marked role: 'admin' shows to
   * both, which is how a tenant owner came to see a Customers tab listing
   * every other customer's name.
   *
   * Presentation only, like `role`. The API already refuses these to anyone
   * who is not admin.system — this stops offering a door that is locked.
   */
  platformOnly?: boolean;
  /**
   * The mirror of platformOnly: shown ONLY to a customer's own owner.
   *
   * Some things exist twice under one label. Settings means the platform's
   * configuration to bloomneo and "my company and my team" to a customer —
   * different pages, different data, same word in the sidebar. Two entries
   * with opposite flags say that plainly; one entry with a conditional `to`
   * would hide it.
   */
  tenantOnly?: boolean;
  /** Starts a labelled group above this item. */
  section?: string;
  /** Ascending. Leave gaps so a later layer can slot between without renumbering. */
  order?: number;
  /** Other routes that light this item up, e.g. /ssl under Domains & SSL. */
  also?: string[];
};

export const items: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true, order: 10 },
];
